import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaArrowRight,
  FaBuilding,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaCreditCard,
  FaExclamationTriangle,
  FaHeadset,
  FaLayerGroup,
  FaLock,
  FaReceipt,
  FaSync,
  FaTimesCircle,
} from "react-icons/fa";
import { hasSubscriptionAccess, useTenant } from "../context/TenantContext";
import { useAppTheme } from "../context/AppThemeContext";
import { getSubscriptionPlans, requestCancellation, requestExtension } from "../api/payments";
import { ADMIN_ROLES, ROLE_SCHOOL, getDashboardPathForRole, normalizeRole } from "../utils/authRole";
import { formatInr, SUBSCRIPTION_PLANS } from "../data/subscriptionPlans";
import SubscriptionPlanCard from "../components/SubscriptionPlanCard";

const getUserRole = () => {
  try {
    const saved = localStorage.getItem("user");
    if (!saved) return "";
    let parsed = saved;
    try {
      parsed = JSON.parse(saved);
    } catch {
      // Login stores the normalized role as a plain string.
    }
    const raw = parsed?.role ?? parsed;
    return normalizeRole(typeof raw === "string" ? raw : String(raw));
  } catch {
    return "";
  }
};

const formatDate = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const DetailItem = ({ icon: Icon, label, value, isDarkMode }) => (
  <div className={isDarkMode ? "rounded-2xl border border-slate-800 bg-slate-900/70 p-4" : "rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"}>
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className={isDarkMode ? "text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500" : "text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400"}>{label}</p>
        <p className={isDarkMode ? "mt-2 text-sm font-bold text-slate-100" : "mt-2 text-sm font-bold text-slate-900"}>{value}</p>
      </div>
      <span className={isDarkMode ? "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-700 bg-slate-800 text-blue-300" : "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-blue-50 text-blue-600"}>
        <Icon aria-hidden="true" />
      </span>
    </div>
  </div>
);

const SubscriptionExpired = () => {
  const { activeTenant, refreshSubscriptionStatus } = useTenant();
  const { isDarkMode } = useAppTheme();
  const navigate = useNavigate();
  const userRole = getUserRole();
  const canRenew = ADMIN_ROLES.includes(userRole) || userRole === ROLE_SCHOOL;
  const canCancel = canRenew;
  const subscription = activeTenant?.subscription || {};
  const accessRestored = hasSubscriptionAccess(subscription);
  const entryRefreshStarted = useRef(false);
  const planLabel = subscription.plan || "Not available";
  const dueAmount = Number(subscription.dueAmount || 0);
  const renewalDate = subscription.renewalDate || subscription.raw?.expiryDate || "";

  const [selectedPlan, setSelectedPlan] = useState("SIX_MONTH");
  const [plans, setPlans] = useState(SUBSCRIPTION_PLANS);
  const [extensionReason, setExtensionReason] = useState("");
  const [extending, setExtending] = useState(false);
  const [extensionRequested, setExtensionRequested] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancellationRequested, setCancellationRequested] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (entryRefreshStarted.current) return;
    entryRefreshStarted.current = true;
    refreshSubscriptionStatus();
    getSubscriptionPlans().then((items) => {
      if (!items.length) return;
      const mappedPlans = items.map((item) => {
        const value = typeof item === "string"
          ? item
          : item?.value || item?.code || item?.plan || item?.planCode || item?.id;
        const template = SUBSCRIPTION_PLANS.find((plan) => plan.value === String(value || "").toUpperCase());
        if (!template) return null;
        return {
          ...template,
          ...((typeof item === "object" && item) || {}),
          value: template.value,
          name: item?.name || item?.planName || template.name,
          price: Number(item?.price ?? item?.amount ?? template.price),
        };
      }).filter(Boolean);
      if (mappedPlans.length) {
        setPlans(mappedPlans);
        setSelectedPlan((current) => mappedPlans.some((plan) => plan.value === current) ? current : mappedPlans[0].value);
      }
    }).catch(() => {});
  }, [refreshSubscriptionStatus]);

  useEffect(() => {
    if (!accessRestored) return;
    const destination = getDashboardPathForRole(userRole);
    navigate(destination && destination !== "/" ? destination : "/admin-home", { replace: true });
  }, [accessRestored, navigate, userRole]);

  const surface = isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white";
  const mutedSurface = isDarkMode ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-slate-50";
  const bodyText = isDarkMode ? "text-slate-300" : "text-slate-600";

  const handleContinue = () => {
    navigate(`/subscription-payment?plan=${encodeURIComponent(selectedPlan)}`);
  };

  const handleExtendTrial = async () => {
    if (extensionRequested) return;
    setExtending(true);
    try {
      await requestExtension({
        reason: extensionReason.trim() || "Need more time to evaluate the platform",
      });
      setExtensionRequested(true);
      toast.success("Extension request submitted for platform review.");
    } catch (error) {
      toast.error(error?.message || "Could not submit the extension request.");
    } finally {
      setExtending(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!canCancel || cancellationRequested) return;
    if (!window.confirm("Submit a subscription cancellation request to the platform team?")) return;

    setCancelling(true);
    try {
      await requestCancellation({
        reason: extensionReason.trim() || "Need to discontinue subscription",
      });
      setCancellationRequested(true);
      toast.success("Cancellation request submitted for review.");
    } catch (error) {
      toast.error(error?.message || "Could not submit the cancellation request.");
    } finally {
      setCancelling(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const refreshed = await refreshSubscriptionStatus();
      if (refreshed && hasSubscriptionAccess(refreshed)) {
        toast.success("Subscription is active. Restoring your workspace.");
      } else {
        toast.info("Subscription status refreshed.");
      }
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className={isDarkMode ? "min-h-full bg-slate-950 py-8 text-slate-100" : "min-h-full bg-slate-50 py-8 text-slate-900"}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <section className="overflow-hidden rounded-3xl bg-gradient-to-br from-blue-700 via-blue-600 to-teal-600 p-6 text-white shadow-2xl shadow-blue-900/20 sm:p-10 lg:p-12">
          <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-blue-100">
                <FaLock className="text-teal-300" /> Platform access restricted
              </span>
              <h1 className="mt-6 text-4xl font-black tracking-tight sm:text-5xl">Your subscription has expired.</h1>
              <p className="mt-5 max-w-2xl text-sm leading-7 text-blue-100 sm:text-base">Renew your ClassNX workspace to restore secure access to school operations.</p>
            </div>
            <div className="flex h-32 w-32 shrink-0 items-center justify-center rounded-3xl border border-white/15 bg-white/10 text-6xl text-teal-300 shadow-2xl shadow-black/20 backdrop-blur sm:h-40 sm:w-40 sm:text-7xl">
              <FaExclamationTriangle aria-hidden="true" />
            </div>
          </div>
        </section>

        <section className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <DetailItem icon={FaBuilding} label="Tenant" value={activeTenant?.name || "Your school"} isDarkMode={isDarkMode} />
          <DetailItem icon={FaLayerGroup} label="Current plan" value={planLabel} isDarkMode={isDarkMode} />
          <DetailItem icon={FaCalendarAlt} label="Expired on" value={formatDate(renewalDate)} isDarkMode={isDarkMode} />
          <DetailItem icon={FaReceipt} label="Outstanding amount" value={dueAmount > 0 ? formatInr(dueAmount) : "Calculated at checkout"} isDarkMode={isDarkMode} />
        </section>

        {canRenew ? (
        <section className={`mt-8 rounded-3xl border p-6 shadow-xl shadow-slate-900/5 sm:p-8 ${surface}`}>
          <div className="mx-auto max-w-3xl text-center">
            <p className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-600"><FaLayerGroup aria-hidden="true" /> Renewal plans</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">Choose your membership plan.</h2>
            <p className={`mt-3 text-sm leading-6 ${bodyText}`}>Select the duration that fits your school and continue to secure payment.</p>
            {dueAmount > 0 && <span className="mt-4 inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-800">Current due: {formatInr(dueAmount)}</span>}
          </div>

          <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {plans.map((plan) => (
              <SubscriptionPlanCard
                key={plan.value}
                plan={plan}
                selected={selectedPlan === plan.value}
                onSelect={setSelectedPlan}
                isDarkMode={isDarkMode}
              />
            ))}
          </div>

          <div className="mt-8 flex flex-col gap-4 rounded-3xl border border-blue-200 bg-blue-50 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">Ready to renew</p>
              <p className="mt-1 text-sm text-blue-800">Selected plan: <span className="font-bold">{plans.find((plan) => plan.value === selectedPlan)?.name} · {plans.find((plan) => plan.value === selectedPlan)?.duration}</span></p>
            </div>
            <button type="button" onClick={handleContinue} className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-700 to-teal-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-blue-700/20 transition hover:-translate-y-0.5 hover:shadow-xl">
              <FaCreditCard aria-hidden="true" /> Continue to payment <FaArrowRight className="text-xs" />
            </button>
          </div>
        </section>
        ) : (
          <section className={`mt-8 rounded-3xl border p-6 text-center shadow-xl shadow-slate-900/5 sm:p-8 ${surface}`}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Subscription expired</p>
            <h2 className="mt-3 text-2xl font-black tracking-tight">Access will be restored by your school administrator.</h2>
            <p className={`mx-auto mt-3 max-w-xl text-sm leading-6 ${bodyText}`}>
              Your school’s subscription has expired. Please ask your school administrator to renew it.
            </p>
            <button type="button" onClick={handleRefresh} disabled={refreshing} className={`mt-6 inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-bold transition hover:bg-slate-50 disabled:opacity-60 ${mutedSurface}`}>
              <FaSync className={refreshing ? "animate-spin" : ""} /> Check subscription status
            </button>
          </section>
        )}

        {canRenew && (
          <section className={`mt-6 grid gap-6 rounded-3xl border p-6 shadow-xl shadow-slate-900/5 lg:grid-cols-[1fr_auto] lg:items-start sm:p-8 ${surface}`}>
            <div>
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-teal-600"><FaHeadset aria-hidden="true" /> Support options</p>
              <h2 className="mt-2 text-xl font-black">Need more time?</h2>
              <p className={`mt-2 max-w-2xl text-sm leading-6 ${bodyText}`}>Request a short extension while your renewal is reviewed.</p>
              {!extensionRequested && (
                <textarea
                  value={extensionReason}
                  onChange={(event) => setExtensionReason(event.target.value)}
                  rows={3}
                  placeholder="Optional: tell us why you need an extension"
                  className={`mt-5 w-full max-w-xl rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-500" : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400"}`}
                />
              )}
            </div>
            <div className="flex w-full flex-row flex-wrap items-center gap-3 lg:w-auto lg:flex-nowrap lg:justify-end">
              <button type="button" onClick={handleExtendTrial} disabled={extending || extensionRequested} className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2.5 text-sm font-bold transition hover:border-blue-300 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60">
                {extensionRequested ? <FaCheckCircle className="text-emerald-600" /> : <FaClock />}
                {extensionRequested ? "Request submitted" : extending ? "Submitting…" : "Request extension"}
              </button>
              {canCancel && (
                <button type="button" onClick={handleCancelSubscription} disabled={cancelling || cancellationRequested} className="inline-flex items-center gap-2 rounded-full border border-rose-300 px-4 py-2.5 text-sm font-bold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60">
                  <FaTimesCircle /> {cancellationRequested ? "Cancellation submitted" : cancelling ? "Submitting…" : "Request cancellation"}
                </button>
              )}
              <button type="button" onClick={handleRefresh} disabled={refreshing} className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-bold transition hover:bg-slate-50 disabled:opacity-60 ${mutedSurface}`}>
                <FaSync className={refreshing ? "animate-spin" : ""} /> Refresh status
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default SubscriptionExpired;
